/* eslint-disable camelcase */
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import AmplifyApiService, { PaginatedResult } from '../../services/AmplifyApiService';
import { SelectionMode } from '../../shared/enums/selection-mode';
import removeUndefinedQueryParams from '../../shared/helpers/remove-undefined-query-params';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';
import { IFootPrintDetails, PCFEntity } from '../../shared/interfaces/IFootprint';
import { IRequest } from '../../shared/interfaces/IRequest';
import { IdentifierType } from '../../shared/types/CompanyIdentifier';
import { CompanyFilterDrodpdownItem } from './company';

interface IRequestPdfDownloadUrl {
  downloadUrl: string;
}

interface IRequestJsonDownloadUrl {
  data: unknown;
}

type RequestPcfProps = {
  componentId?: number;
  bpn?: string;
  companyId?: number;
  productId?: string;
  productName?: string;
  message?: string;
};

type AnswerCatenaXPcfRequestProps = {
  requestId: string;
  componentId: number;
};

type GetAnswerRequestProps = {
  requestId: string;
  token: string;
};

export type ExportProductTasksProps = {
  productTaskIds: string[];
  selectionMode: SelectionMode;
  filter?: Partial<GetTasksParams>;
};

export type ExportProductTasksResponse = {
  fileUrl: string;
};

export type AnswerPcfRequestProps = {
  requestId: string;
  isDraft: boolean;
  token: string;
  product_description: string;
  region: string;
  country: string | null;
  product_id: string;
  product_code_cpc: string;
  product_name: string;
  date_created: string;
  partial_full_pcf: string;
  pcf: PCFEntity;
  company_identifiers: IdentifierType[];
  pcf_legal_statement?: string;
};

export type GetTasksParams = {
  status?: string;
  search?: string;
  skip?: number;
  size?: number;
  sort?: string;
  senderCompanyIds?: string;
  senderCompanyBPNs?: string;
};

const amplifyApiService = new AmplifyApiService();

const REQUESTS_BASE_PATH = 'requests';
const PRODUCT_TASKS_BASE_PATH = `${REQUESTS_BASE_PATH}/product-tasks`;
const COMPANIES_FROM_PRODUCT_TASKS_BASE_PATH = `${REQUESTS_BASE_PATH}/product-tasks/companies`;
const COMPONENT_TASKS_BASE_PATH = `${REQUESTS_BASE_PATH}/component-tasks`;

export const useGetPaginatedProductTasks = (
  params: GetTasksParams = {},
  options: UseQueryOptions<PaginatedResult<IRequest[]>, Error> = {},
): UseQueryResult<PaginatedResult<IRequest[]>, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: PRODUCT_TASKS_BASE_PATH,
    options: {
      queryParams: removeUndefinedQueryParams(params),
    },
  };

  const query = useQuery<PaginatedResult<IRequest[]>, Error>({
    queryKey: [PRODUCT_TASKS_BASE_PATH, params],
    queryFn: async (): Promise<PaginatedResult<IRequest[]>> => {
      const response = await amplifyApiService.paginatedGet<IRequest[]>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useGetCompaniesFromProductTasks = (
  options: UseQueryOptions<CompanyFilterDrodpdownItem[], Error> = {},
): UseQueryResult<CompanyFilterDrodpdownItem[], Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: COMPANIES_FROM_PRODUCT_TASKS_BASE_PATH,
  };

  const query = useQuery<CompanyFilterDrodpdownItem[], Error>({
    queryKey: [COMPANIES_FROM_PRODUCT_TASKS_BASE_PATH],
    queryFn: async (): Promise<CompanyFilterDrodpdownItem[]> => {
      const response = await amplifyApiService.get<CompanyFilterDrodpdownItem[]>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useGetPaginatedComponentTasks = (
  filters: GetTasksParams = {},
  options: UseQueryOptions<PaginatedResult<IRequest[]>, Error> = {},
): UseQueryResult<PaginatedResult<IRequest[]>, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: COMPONENT_TASKS_BASE_PATH,
    options: {
      queryParams: removeUndefinedQueryParams(filters),
    },
  };

  const query = useQuery<PaginatedResult<IRequest[]>, Error>({
    queryKey: [COMPONENT_TASKS_BASE_PATH, filters],
    queryFn: async (): Promise<PaginatedResult<IRequest[]>> => {
      const response = await amplifyApiService.paginatedGet<IRequest[]>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useCreatePcfRequest = (
  options: UseMutationOptions<void, Error, RequestPcfProps> = {},
): UseMutationResult<void, Error, RequestPcfProps> => {
  const mutation = useMutation<void, Error, RequestPcfProps>({
    mutationFn: async (request: RequestPcfProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: REQUESTS_BASE_PATH,
        options: {
          body: {
            ...request,
          },
        },
      };

      await amplifyApiService.post<RequestPcfProps, { id: number }>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useGetRequestPdfDownloadUrl = (
  id?: number,
  options: UseQueryOptions<IRequestPdfDownloadUrl, Error> = {},
): UseQueryResult<IRequestPdfDownloadUrl, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${REQUESTS_BASE_PATH}/${id}/get-download-pdf`,
    options: {},
  };

  const query = useQuery<IRequestPdfDownloadUrl, Error>({
    queryKey: [REQUESTS_BASE_PATH, id, 'get-download-pdf'],
    queryFn: async (): Promise<IRequestPdfDownloadUrl> => {
      const response = await amplifyApiService.get<IRequestPdfDownloadUrl>(amplifyApiConfig);

      return response;
    },
    enabled: false,
    ...options,
  });

  return query;
};

export const useGetRequestJsonDownloadUrl = (
  id?: number,
  options: UseQueryOptions<IRequestJsonDownloadUrl, Error> = {},
): UseQueryResult<IRequestJsonDownloadUrl, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${REQUESTS_BASE_PATH}/${id}/get-download-json`,
    options: {},
  };

  const query = useQuery<IRequestJsonDownloadUrl, Error>({
    queryKey: [REQUESTS_BASE_PATH, id, 'get-download-json'],
    queryFn: async (): Promise<IRequestJsonDownloadUrl> => {
      const response = await amplifyApiService.get<IRequestJsonDownloadUrl>(amplifyApiConfig);

      return response;
    },
    enabled: false,
    ...options,
  });

  return query;
};

export const useAnswerCatenaXPcfRequest = (
  options: UseMutationOptions<void, Error, AnswerCatenaXPcfRequestProps> = {},
): UseMutationResult<void, Error, AnswerCatenaXPcfRequestProps> => {
  const mutation = useMutation<void, Error, AnswerCatenaXPcfRequestProps>({
    mutationFn: async (props: AnswerCatenaXPcfRequestProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${REQUESTS_BASE_PATH}/${props.requestId}/answer-pcf-request`,
        options: {
          body: { componentId: props.componentId },
        },
      };

      await amplifyApiService.put<AnswerCatenaXPcfRequestProps, Record<string, never>>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return mutation;
};

export const useGetAnswerRequest = (
  request: GetAnswerRequestProps,
  options: UseQueryOptions<IFootPrintDetails, Error> = {},
): UseQueryResult<IFootPrintDetails, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${REQUESTS_BASE_PATH}/${request.requestId}/answer-request`,
    options: {
      queryParams: {
        token: request.token,
      },
    },
  };

  const query = useQuery<IFootPrintDetails, Error>({
    queryKey: [REQUESTS_BASE_PATH, request.requestId, request.token, 'answer-request'],
    queryFn: async (): Promise<IFootPrintDetails> => {
      const response = await amplifyApiService.get<IFootPrintDetails>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useAnswerPcfRequest = (
  options: UseMutationOptions<void, Error, AnswerPcfRequestProps> = {},
): UseMutationResult<void, Error, AnswerPcfRequestProps> => {
  const mutation = useMutation<void, Error, AnswerPcfRequestProps>({
    mutationFn: async (props: AnswerPcfRequestProps) => {
      const { requestId, ...body } = props;
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${REQUESTS_BASE_PATH}/${requestId}/answer-request`,
        options: {
          body,
        },
      };

      await amplifyApiService.post<AnswerPcfRequestProps, Record<string, never>>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useExportProductTasks = (
  options: UseMutationOptions<ExportProductTasksResponse, Error, ExportProductTasksProps> = {},
): UseMutationResult<ExportProductTasksResponse, Error, ExportProductTasksProps> => {
  const mutation = useMutation<ExportProductTasksResponse, Error, ExportProductTasksProps>({
    mutationFn: async (props: ExportProductTasksProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${REQUESTS_BASE_PATH}/export-product-tasks`,
        options: {
          body: props,
        },
      };

      return await amplifyApiService.post<ExportProductTasksProps, ExportProductTasksResponse>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return mutation;
};
